// src/components/ScreenGuard.js
// Enhanced screenshot/copy protection for web apps
// - Disables text selection and copying when protection is active
// - Blocks right-click context menu
// - Blocks print functionality
// - Auto-blurs content when mouse leaves window (deters screenshot tools)
// - Blurs on visibility change (tab switch detection)
// - Admins and users with canScreenshot permission are exempt

import React from 'react';
import { useUser } from '../context/UserProvider';

/**
 * ScreenGuard Component
 * Wraps protected content with CSS-based copy/screenshot prevention.
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to protect
 * @param {boolean} props.protected - If true, apply protection (default: true)
 */
export function ScreenGuard({ children, protected: isProtected = true }) {
    const { claims, settings } = useUser();
    const [isBlurred, setIsBlurred] = React.useState(false);

    // Admins can always copy/screenshot
    const isAdmin = !!claims?.admin || !!claims?.managerPlus;
    // Check user's canScreenshot permission
    const canScreenshot = settings?.canScreenshot === true;

    // Apply protection only if:
    // 1. Protection is requested (isProtected)
    // 2. User is NOT admin
    // 3. User does NOT have canScreenshot permission
    const shouldProtect = isProtected && !isAdmin && !canScreenshot;

    // Block right-click, keyboard shortcuts, and add blur detection
    React.useEffect(() => {
        if (!shouldProtect) return;

        const handleContextMenu = (e) => {
            e.preventDefault();
            return false;
        };

        const handleKeyDown = (e) => {
            // Block Ctrl+C, Ctrl+A, Ctrl+P (copy, select all, print)
            if (e.ctrlKey && ['c', 'a', 'p'].includes(e.key.toLowerCase())) {
                e.preventDefault();
                return false;
            }
            // Block PrintScreen
            if (e.key === 'PrintScreen') {
                e.preventDefault();
                // Also blur momentarily when PrintScreen is pressed
                setIsBlurred(true);
                setTimeout(() => setIsBlurred(false), 2000);
                return false;
            }
        };

        const handleBeforePrint = (e) => {
            e.preventDefault();
            return false;
        };

        // Auto-blur when mouse leaves the document/window
        const handleMouseLeave = (e) => {
            // Only blur if mouse actually left the document
            if (e.clientY <= 0 || e.clientX <= 0 ||
                e.clientX >= window.innerWidth || e.clientY >= window.innerHeight) {
                setIsBlurred(true);
            }
        };

        // Blur when window/tab loses focus (visibility change)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                setIsBlurred(true);
            }
        };

        // Blur when window loses focus
        const handleWindowBlur = () => {
            setIsBlurred(true);
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('beforeprint', handleBeforePrint);
        document.addEventListener('mouseleave', handleMouseLeave);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleWindowBlur);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('beforeprint', handleBeforePrint);
            document.removeEventListener('mouseleave', handleMouseLeave);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [shouldProtect]);

    // Click to restore content
    const handleClick = () => {
        if (isBlurred) {
            setIsBlurred(false);
        }
    };

    if (!shouldProtect) {
        return <>{children}</>;
    }

    return (
        <div
            className="screen-guard"
            style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                filter: isBlurred ? 'blur(20px)' : 'none',
                transition: 'filter 0.15s ease-out',
                position: 'relative',
            }}
            onClick={handleClick}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
        >
            {children}
            {/* Blur overlay message */}
            {isBlurred && (
                <div
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        color: '#ffffff',
                        padding: '20px 40px',
                        borderRadius: '12px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        zIndex: 9999,
                        textAlign: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    Click to continue viewing
                </div>
            )}
            {/* Print protection - hide content when printing */}
            <style>{`
        @media print {
          .screen-guard {
            display: none !important;
          }
          body::after {
            content: "Printing is disabled for security reasons.";
            display: block;
            text-align: center;
            padding: 50px;
            font-size: 24px;
            color: #666;
          }
        }
      `}</style>
        </div>
    );
}

export default ScreenGuard;
